import type { ReactNode } from 'react';

import cyrinoIcon from '../../assets/cyrino-icon.svg';
import cyrinoWordmarkOnDark from '../../assets/cyrino-wordmark-on-dark.svg';

interface SignInFrameProps {
  /** One line shown on the navy panel from `lg` up; the header band on a phone stays logo only. */
  tagline?: string;
  children: ReactNode;
}

/**
 * The two-part frame of every sign-in page: a navy brand panel and the
 * form area. The panel is a header band on a phone and a full-height column
 * from `lg` up. Navy is the brand's own colour and stays navy in both themes,
 * so it always shows the white-lettered logo with its coloured mark; the form area
 * follows the theme. The window never scrolls: only the form area may, on a
 * screen too short for its content.
 */
export function SignInFrame({ tagline, children }: SignInFrameProps) {
  return (
    <div className="flex h-full flex-col overflow-hidden bg-background lg:flex-row">
      <aside className="relative flex shrink-0 items-center overflow-hidden bg-navy-950 px-6 py-5 text-primary-foreground lg:w-[44%] lg:max-w-2xl lg:flex-col lg:items-start lg:justify-between lg:px-12 lg:py-12">
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-[radial-gradient(circle_at_15%_0%,var(--blue-700),transparent_65%)]"
        />
        <div
          aria-hidden="true"
          className="absolute -bottom-40 -right-40 hidden size-[34rem] rounded-full border border-primary-foreground/10 lg:block"
        />
        <div
          aria-hidden="true"
          className="absolute -bottom-24 -right-24 hidden size-[22rem] rounded-full border border-primary-foreground/10 lg:block"
        />
        <img
          src={cyrinoIcon}
          alt=""
          aria-hidden="true"
          className="absolute -bottom-10 -right-10 hidden h-72 w-auto opacity-[0.06] brightness-0 invert lg:block"
        />
        <img src={cyrinoWordmarkOnDark} alt="Cyrino" className="relative h-8 w-auto lg:h-12" />
        {tagline ? (
          <p className="relative hidden max-w-sm text-xl font-medium leading-snug text-primary-foreground/85 lg:block">
            {tagline}
          </p>
        ) : null}
      </aside>

      <main className="flex min-h-0 flex-1 items-start justify-center overflow-y-auto px-6 py-6 lg:items-center lg:py-8">
        <div className="w-full max-w-sm motion-safe:animate-rise-in">{children}</div>
      </main>
    </div>
  );
}
